import { faTag } from '@fortawesome/pro-solid-svg-icons';
import { isComponentSelfContained } from '@supply-mover-2-0/core';
import { useState } from 'react';
import { Icon } from '../../../components/base/Icon';
import { Text } from '../../../components/base/Text';
import { Card } from '../../../components/display/Card';
import { Input } from '../../../components/forms/Input';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import type { FieldConfiguration, ListBuilderConfiguration } from '../../types';
import { ColorConfigurator } from './components/ColorConfigurator';
import { ConditionalVisibilityConfigurator } from './components/ConditionalVisibilityConfigurator';
import { CustomComponentConfigurator } from './components/CustomComponentConfigurator';
import { DataSourceConfigurator } from './components/DataSourceConfigurator';
import { DisplayConfigConfigurator } from './components/DisplayConfigConfigurator';
import { DisplayStyleSelector } from './components/DisplayStyleSelector';
import { FormatOptionsConfigurator } from './components/FormatOptionsConfigurator';
import { LinkConfigurator } from './components/LinkConfigurator';
import { StyleConfigurator } from './components/StyleConfigurator';
import { ValueMappingConfigurator } from './components/ValueMappingConfigurator';

interface FieldConfiguratorProps {
	field: FieldConfiguration | null;
	fieldIndex: number;
	configuration?: ListBuilderConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	isModalOpen?: boolean;
	// When true the configurator renders inert/dimmed so users can inspect
	// the saved field config on a read-only list (built-in default,
	// non-editable saved search) without being able to change anything.
	// The scroll container stays interactive so the user can still scroll
	// through the full configuration.
	isReadOnly?: boolean;
}

export function FieldConfigurator({
	field,
	fieldIndex,
	configuration,
	onUpdateField,
	isModalOpen = false,
	isReadOnly = false,
}: FieldConfiguratorProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [previewValue, setPreviewValue] = useState('Sample Text');

	if (!field) {
		return (
			<div className="flex-1 flex justify-center items-center p-6">
				<Text className="text-base text-center" color="secondary">
					Select a field to configure
				</Text>
			</div>
		);
	}

	return (
		<div className={cn('h-full overflow-y-auto', colors.classes.bgSecondary)}>
			{/* pointer-events-none + opacity-60 live on this INNER wrapper
			    rather than the scroll container above, so wheel/touch events
			    still reach the overflow-y-auto element and the panel stays
			    scrollable on read-only lists. */}
			<div
				className={cn('p-4', isReadOnly && 'pointer-events-none opacity-60')}
			>
				<DisplayStyleSelector
					field={field}
					onUpdateField={onUpdateField}
					previewValue={previewValue}
					configuration={configuration}
				/>

				{field.displayStyle !== 'line_break' &&
					!(
						field.displayStyle === 'custom' &&
						field.customComponent?.id != null &&
						isComponentSelfContained(field.customComponent.id)
					) && (
						<>
							<DataSourceConfigurator
								field={field}
								configuration={configuration}
								onUpdateField={onUpdateField}
								onPreviewValueChange={setPreviewValue}
								isModalOpen={isModalOpen}
							/>

							<StyleConfigurator
								field={field}
								onUpdateField={onUpdateField}
								isModalOpen={isModalOpen}
							/>

							<ColorConfigurator field={field} onUpdateField={onUpdateField} />

							<ValueMappingConfigurator
								field={field}
								onUpdateField={onUpdateField}
								isModalOpen={isModalOpen}
							/>
						</>
					)}

				<FormatOptionsConfigurator
					field={field}
					configuration={configuration}
					onUpdateField={onUpdateField}
					isModalOpen={isModalOpen}
				/>

				<CustomComponentConfigurator
					field={field}
					onUpdateField={onUpdateField}
					isModalOpen={isModalOpen}
				/>

				<DisplayConfigConfigurator
					field={field}
					onUpdateField={onUpdateField}
					isModalOpen={isModalOpen}
				/>

				{field.displayStyle !== 'line_break' && (
					<LinkConfigurator
						field={field}
						configuration={configuration}
						onUpdateField={onUpdateField}
						isModalOpen={isModalOpen}
					/>
				)}

				{/* Field Label (for kanban cards and grid sections) */}
				{field.displayStyle !== 'line_break' && (
					<Card className="mb-4">
						<div className="p-4">
							<div className="flex items-center mb-3">
								<Icon
									icon={faTag}
									size="sm"
									className={`mr-2 ${colors.classes.textPrimary}`}
								/>
								<Text className="text-base font-semibold">Field Label</Text>
							</div>
							<Input
								value={field.label ?? ''}
								onChange={(e) => {
									onUpdateField({
										label: e.target.value !== '' ? e.target.value : undefined,
									});
								}}
								placeholder="e.g., Status, Customer Name"
							/>
							<Text className="text-xs mt-1" color="secondary">
								Optional label displayed above the field in kanban cards and
								grid sections
							</Text>
						</div>
					</Card>
				)}

				{/* Conditional Visibility */}
				{field.displayStyle !== 'line_break' && (
					<ConditionalVisibilityConfigurator
						field={field}
						onUpdateField={onUpdateField}
					/>
				)}
			</div>
		</div>
	);
}
